import type { ConversationRef } from '../types/transport';
import type { Context } from '../integrations/context-types';
import type { ShellAction } from './shell-actions';
import type { NextPromptState, PaneId, ShellState } from './shell-state';
import {
  derivedFingerprint,
  userContextFingerprint,
} from './context-fingerprints';

/** The offered derived contexts that may show: not already present as a
 *  kept-pinned chip, and not dismissed on the current route. */
function admitDerived(
  offered: readonly Context[],
  keptPinned: readonly Context[],
  dismissed: readonly string[] | undefined
): Context[] {
  const kept = new Set(keptPinned.map(derivedFingerprint));
  const gone = new Set(dismissed ?? []);
  return offered.filter((c) => {
    const fp = derivedFingerprint(c);
    return !kept.has(fp) && !gone.has(fp);
  });
}

/** Applies `next` to a pane's next-prompt state, returning `state` untouched
 *  when the pane is missing or `next` hands back the same object. */
function withNextPrompt(
  state: ShellState,
  paneId: PaneId,
  next: (prev: NextPromptState) => NextPromptState
): ShellState {
  const pane = state.launcher.panes[paneId];
  if (!pane) return state;
  const prev = pane.nextPrompt ?? { requestId: null, suggestion: null };
  const nextPrompt = next(prev);
  if (nextPrompt === prev) return state;
  return {
    ...state,
    launcher: {
      ...state.launcher,
      panes: { ...state.launcher.panes, [paneId]: { ...pane, nextPrompt } },
    },
  };
}

function withDismissal(
  bucket: Record<string, string[]>,
  sig: string,
  fp: string
): Record<string, string[]> {
  const current = bucket[sig] ?? [];
  if (current.includes(fp)) return bucket;
  return { ...bucket, [sig]: [...current, fp] };
}

function withoutDismissal(
  bucket: Record<string, string[]>,
  sig: string | null,
  fp: string
): Record<string, string[]> {
  if (!sig) return bucket;
  const current = bucket[sig];
  if (!current?.includes(fp)) return bucket;
  const filtered = current.filter((entry) => entry !== fp);
  if (filtered.length === 0) {
    const next = { ...bucket };
    delete next[sig];
    return next;
  }
  return { ...bucket, [sig]: filtered };
}

/** Pure reducer over ShellState. Every action is past tense (e.g.
 *  `AGENT_SELECTED`) — they record events that happened, not commands.
 *  Adding a new action requires extending the switch; the exhaustive
 *  default with `never` keeps that honest. */
export function shellReducer(
  state: ShellState,
  action: ShellAction
): ShellState {
  switch (action.type) {
    case 'AGENTS_LOAD_REQUESTED':
      return { ...state, agents: { ...state.agents, status: 'loading' } };

    case 'AGENTS_LOADED': {
      const byId: Record<string, ShellState['agents']['byId'][string]> = {};
      const allIds: string[] = [];
      for (const a of action.agents) {
        byId[a.id] = a;
        allIds.push(a.id);
      }
      return {
        ...state,
        agents: {
          byId,
          allIds,
          status: 'ready',
          defaultAgentId: action.defaultAgentId,
        },
      };
    }

    case 'AGENTS_LOAD_FAILED':
      return { ...state, agents: { ...state.agents, status: 'error' } };

    case 'CONVERSATIONS_LOADED': {
      const byId = { ...state.conversations.byId };
      const ids: string[] = [];
      for (const c of action.conversations) {
        byId[c.id] = c;
        ids.push(c.id);
      }
      const byAgentId = {
        ...state.conversations.byAgentId,
        [action.agentId]: ids,
      };
      return { ...state, conversations: { byId, byAgentId } };
    }

    case 'CONVERSATION_FINISHED': {
      if (!action.ref) return state;
      const byId = {
        ...state.conversations.byId,
        [action.ref.id]: action.ref,
      };
      const list = state.conversations.byAgentId[action.agentId] ?? [];
      const byAgentId = {
        ...state.conversations.byAgentId,
        [action.agentId]: list.includes(action.ref.id)
          ? list
          : [action.ref.id, ...list],
      };
      return { ...state, conversations: { byId, byAgentId } };
    }

    case 'CONVERSATION_LIST_LOADED': {
      const byId: Record<string, ConversationRef> = {
        ...state.conversations.byId,
      };
      const byAgentId: Record<string, string[]> = {
        ...state.conversations.byAgentId,
      };
      const grouped: Record<string, string[]> = {};
      for (const c of action.conversations) {
        byId[c.id] = c;
        (grouped[c.agentId] ??= []).push(c.id);
      }
      for (const [agentId, ids] of Object.entries(grouped)) {
        byAgentId[agentId] = ids;
      }
      return { ...state, conversations: { byId, byAgentId } };
    }

    case 'CONVERSATION_RENAMED': {
      const existing = state.conversations.byId[action.conversationId];
      if (!existing) return state;
      return {
        ...state,
        conversations: {
          ...state.conversations,
          byId: {
            ...state.conversations.byId,
            [action.conversationId]: { ...existing, title: action.title },
          },
        },
      };
    }

    case 'CONVERSATION_DELETED': {
      // Drop the deleted id from byId and from every agent's id list.
      const byId = { ...state.conversations.byId };
      delete byId[action.conversationId];
      const byAgentId: Record<string, string[]> = {};
      for (const [agentId, ids] of Object.entries(
        state.conversations.byAgentId
      )) {
        byAgentId[agentId] = ids.filter((id) => id !== action.conversationId);
      }
      const wasOpen = state.selection.conversationId === action.conversationId;
      return {
        ...state,
        conversations: { byId, byAgentId },
        selection: wasOpen
          ? { ...state.selection, conversationId: null }
          : state.selection,
      };
    }

    case 'AGENT_SELECTED':
      return {
        ...state,
        selection: {
          agentId: action.agentId,
          conversationId: null,
          resetNonce: state.selection.resetNonce + 1,
        },
      };

    case 'CONVERSATION_SELECTED':
      return {
        ...state,
        selection: {
          ...state.selection,
          conversationId: action.conversationId,
        },
      };

    case 'CONVERSATION_NEW_REQUESTED':
      // New Chat keeps pinned user-contexts, drops unpinned ones. Mirrors
      // V1 parity: pinned chips represent an explicit user choice that
      // should survive the reset.
      return {
        ...state,
        selection: {
          ...state.selection,
          conversationId: null,
          resetNonce: state.selection.resetNonce + 1,
        },
        launcher: {
          ...state.launcher,
          panes: Object.fromEntries(
            Object.entries(state.launcher.panes).map(([paneId, pane]) => [
              paneId,
              { ...pane, toneId: null },
            ])
          ),
        },
        contexts: {
          ...state.contexts,
          user: state.contexts.user.filter((c) => c.pinned === true),
        },
      };

    case 'CONVERSATION_OPENED':
      // Atomic agent + conversation switch from the drawer. Closes the drawer
      // in the same dispatch so callers don't need a follow-up DRAWER_CLOSED.
      return {
        ...state,
        selection: {
          agentId: action.agentId,
          conversationId: action.conversationId,
          resetNonce: state.selection.resetNonce,
        },
        drawer: { ...state.drawer, isOpen: false },
      };

    case 'DRAWER_OPENED':
      return { ...state, drawer: { ...state.drawer, isOpen: true } };

    case 'DRAWER_CLOSED':
      return { ...state, drawer: { ...state.drawer, isOpen: false } };

    case 'DRAWER_VIEW_CHANGED':
      return { ...state, drawer: { ...state.drawer, view: action.view } };

    case 'DRAWER_PINNED':
      return { ...state, drawer: { ...state.drawer, pinned: action.pinned } };

    case 'LAUNCHER_OPENED':
      return { ...state, launcher: { ...state.launcher, isOpen: true } };

    case 'LAUNCHER_CLOSED':
      return { ...state, launcher: { ...state.launcher, isOpen: false } };

    case 'LAUNCHER_TOGGLED':
      return {
        ...state,
        launcher: { ...state.launcher, isOpen: !state.launcher.isOpen },
      };

    case 'PANE_COMPOSER_TEXT_CHANGED': {
      const prev = state.launcher.panes[action.paneId];
      if (!prev) return state;
      return {
        ...state,
        launcher: {
          ...state.launcher,
          panes: {
            ...state.launcher.panes,
            [action.paneId]: {
              ...prev,
              composerText: action.text,
              // Typing (or Tab filling the composer) retires the guess and
              // any request still in flight for it.
              ...(action.text !== '' &&
              (prev.nextPrompt?.requestId || prev.nextPrompt?.suggestion)
                ? { nextPrompt: { requestId: null, suggestion: null } }
                : {}),
            },
          },
        },
      };
    }

    case 'PANE_TONE_CHANGED': {
      const prev = state.launcher.panes[action.paneId];
      if (!prev) return state;
      return {
        ...state,
        launcher: {
          ...state.launcher,
          panes: {
            ...state.launcher.panes,
            [action.paneId]: { ...prev, toneId: action.toneId },
          },
        },
      };
    }

    case 'PANE_WEB_SEARCH_CHANGED': {
      const prev = state.launcher.panes[action.paneId];
      if (!prev) return state;
      return {
        ...state,
        launcher: {
          ...state.launcher,
          panes: {
            ...state.launcher.panes,
            [action.paneId]: { ...prev, webSearchEnabled: action.enabled },
          },
        },
      };
    }

    case 'PANE_NEXT_PROMPT_REQUESTED':
      return withNextPrompt(state, action.paneId, () => ({
        requestId: action.requestId,
        suggestion: null,
      }));

    case 'PANE_NEXT_PROMPT_RECEIVED':
      return withNextPrompt(state, action.paneId, (prev) =>
        prev.requestId === action.requestId
          ? { requestId: null, suggestion: action.suggestion }
          : prev
      );

    case 'PANE_NEXT_PROMPT_CLEARED':
      return withNextPrompt(state, action.paneId, () => ({
        requestId: null,
        suggestion: null,
      }));

    case 'PANE_SCROLL_POSITION_CHANGED': {
      const prev = state.launcher.panes[action.paneId];
      if (!prev) return state;
      return {
        ...state,
        launcher: {
          ...state.launcher,
          panes: {
            ...state.launcher.panes,
            [action.paneId]: { ...prev, scrollPositionPx: action.px },
          },
        },
      };
    }

    case 'CONTEXT_USER_ATTACHED': {
      // V1 parity (AskMooProvider.addContext): re-attaching the same
      // entity is a no-op so the user can't accidentally stack two
      // chips for the same record by clicking the same source twice
      // or by re-dragging it from another panel.
      const incoming = action.context;
      if (incoming.kind === 'entity') {
        const already = state.contexts.user.some(
          (c) =>
            c.kind === 'entity' &&
            c.entityType === incoming.entityType &&
            c.entityId === incoming.entityId
        );
        if (already) return state;
      }
      return {
        ...state,
        contexts: {
          ...state.contexts,
          user: [...state.contexts.user, incoming],
        },
      };
    }

    case 'CONTEXT_USER_REMOVED':
      return {
        ...state,
        contexts: {
          ...state.contexts,
          user: state.contexts.user.filter((_, i) => i !== action.index),
        },
      };

    case 'CONTEXTS_USER_HYDRATED': {
      // Merge instead of replace: any context the user attached during
      // the agents-catalog loading window is preserved. Hydrated entries
      // append after the live array so the user's recent additions stay
      // at their visible position; matching fingerprints from the
      // persisted list are dropped (the live entry already represents
      // the same thing, often with newer data).
      const existing = new Set(state.contexts.user.map(userContextFingerprint));
      const additions = action.contexts.filter(
        (c) => !existing.has(userContextFingerprint(c))
      );
      if (additions.length === 0) return state;
      return {
        ...state,
        contexts: {
          ...state.contexts,
          user: [...state.contexts.user, ...additions],
        },
      };
    }

    case 'CONTEXT_PINNED':
      return {
        ...state,
        contexts: {
          ...state.contexts,
          user: state.contexts.user.map((c, i) =>
            i === action.index ? { ...c, pinned: true } : c
          ),
        },
      };

    case 'CONTEXT_UNPINNED': {
      // Drop the `pinned` flag entirely rather than setting it to false —
      // keeps the persisted shape minimal and matches V1's optional-flag idiom.
      return {
        ...state,
        contexts: {
          ...state.contexts,
          user: state.contexts.user.map((c, i) => {
            if (i !== action.index) return c;
            const { pinned: _pinned, ...rest } = c;
            return rest;
          }),
        },
      };
    }

    case 'CONTEXT_DERIVED_SET': {
      // Pinned derived chips survive route changes (V1 parity: pinning is
      // an explicit "carry across navigations" gesture). Dedup the incoming
      // list against the kept-pinned entries by (entityType,entityId) when
      // both are present — otherwise by view + route — so the same chip
      // doesn't show twice if the host re-derives on the same route.
      const sig = action.routeSignature ?? null;
      const keptPinned = state.contexts.derived.filter(
        (c) => c.pinned === true
      );
      return {
        ...state,
        contexts: {
          ...state.contexts,
          derived: [
            ...keptPinned,
            ...admitDerived(
              action.contexts,
              keptPinned,
              sig ? state.contexts.derivedDismissed?.[sig] : undefined
            ),
          ],
          derivedOffered: action.contexts,
          lastDerivedRouteSignature: sig,
        },
      };
    }

    case 'CONTEXT_DERIVED_RESTORED': {
      // The user re-attached a chip they dismissed on this route: forget
      // the dismissal and re-admit it from what the host last offered, in
      // the host's order.
      const sig = state.contexts.lastDerivedRouteSignature ?? null;
      const dismissedMap = state.contexts.derivedDismissed ?? {};
      const derivedDismissed = withoutDismissal(
        dismissedMap,
        sig,
        derivedFingerprint(action.context)
      );
      if (!sig || derivedDismissed === dismissedMap) {
        return state;
      }
      const keptPinned = state.contexts.derived.filter(
        (c) => c.pinned === true
      );
      return {
        ...state,
        contexts: {
          ...state.contexts,
          derived: [
            ...keptPinned,
            ...admitDerived(
              state.contexts.derivedOffered ?? [],
              keptPinned,
              derivedDismissed[sig]
            ),
          ],
          derivedDismissed,
        },
      };
    }

    case 'CONTEXT_DERIVED_PINNED': {
      const target = state.contexts.derived[action.index];
      return {
        ...state,
        contexts: {
          ...state.contexts,
          derived: state.contexts.derived.map((c, i) =>
            i === action.index ? { ...c, pinned: true } : c
          ),
          // Re-pinning clears any earlier dismissal of the same fingerprint
          // on the current route — the user just told us they want it.
          derivedDismissed: target
            ? withoutDismissal(
                state.contexts.derivedDismissed ?? {},
                state.contexts.lastDerivedRouteSignature ?? null,
                derivedFingerprint(target)
              )
            : state.contexts.derivedDismissed ?? {},
        },
      };
    }

    case 'CONTEXT_DERIVED_UNPINNED':
      return {
        ...state,
        contexts: {
          ...state.contexts,
          derived: state.contexts.derived.map((c, i) => {
            if (i !== action.index) return c;
            const { pinned: _pinned, ...rest } = c;
            return rest;
          }),
        },
      };

    case 'CONTEXT_DERIVED_REMOVED': {
      const removed = state.contexts.derived[action.index];
      const sig = state.contexts.lastDerivedRouteSignature ?? null;
      const dismissedMap = state.contexts.derivedDismissed ?? {};
      return {
        ...state,
        contexts: {
          ...state.contexts,
          derived: state.contexts.derived.filter((_, i) => i !== action.index),
          derivedDismissed:
            removed && sig
              ? withDismissal(dismissedMap, sig, derivedFingerprint(removed))
              : dismissedMap,
        },
      };
    }

    case 'STREAM_ERROR_OCCURRED':
      return {
        ...state,
        errors: { ...state.errors, [action.conversationKey]: action.error },
      };

    case 'STREAM_ERROR_CLEARED': {
      const next = { ...state.errors };
      delete next[action.conversationKey];
      return { ...state, errors: next };
    }

    case 'STORAGE_HYDRATED':
      return { ...state, storageHydrated: true };

    case 'MESSAGE_REPLAY_QUEUED':
      return { ...state, pendingReplay: action.text };

    case 'MESSAGE_REPLAY_CONSUMED':
      return { ...state, pendingReplay: null };

    case 'MESSAGE_REPLAY_CLEARED':
      return { ...state, pendingReplay: null };

    case 'LAYOUT_MODE_CHANGED':
      return { ...state, layoutMode: action.mode };

    default: {
      const _exhaustive: never = action;
      void _exhaustive;
      return state;
    }
  }
}
